import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  CircularProgress,
  Alert
} from '@mui/material';
import { ai } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

interface Message {
  id: number;
  content: string;
  is_user: boolean;
  created_at: string;
  metadata: {
    warnings?: string[];
  };
}

interface Conversation {
  id: number;
  messages: Message[];
  scenario: string;
}

const ChatbotPage: React.FC = () => {
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<null | HTMLDivElement>(null);
  const { user } = useAuth();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    startNewConversation();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [conversation?.messages]);

  const startNewConversation = async () => {
    try {
      setLoading(true);
      const response = await ai.startConversation();
      setConversation(response.data.conversation);
    } catch (err) {
      setError('Failed to start conversation');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() || !conversation) return;

    try {
      setLoading(true);
      const response = await ai.sendMessage(conversation.id, message);
      
      // Update conversation with both user message and AI response
      setConversation(prev => prev ? {
        ...prev,
        messages: [...prev.messages, 
          // Add user message
          { 
            id: Date.now(),
            content: message,
            is_user: true,
            created_at: new Date().toISOString(),
            metadata: {}
          },
          // Add AI response
          {
            id: Date.now() + 1,
            content: response.data.message, // Changed from response.data
            is_user: false,
            created_at: new Date().toISOString(),
            metadata: { warnings: response.data.warnings }
          }
        ]
      } : null);
      setMessage('');
    } catch (err) {
      setError('Failed to send message');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ 
      maxWidth: '1000px',
      width: '90%',
      margin: '0 auto',
      pt: 4,
      pb: 8
    }}>
      <Paper elevation={3} sx={{ 
        p: 4,
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        minHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        border: '1px solid rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(10px)'
      }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 4 }}>
          AI Role-Play Assistant
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ 
          flex: 1, 
          overflowY: 'auto',
          mb: 3,
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
          borderRadius: 2,
          p: 3
        }}>
          <List>
            {conversation?.messages.map((msg, index) => (
              <ListItem
                key={msg.id || index}
                sx={{
                  flexDirection: 'column',
                  alignItems: msg.is_user ? 'flex-end' : 'flex-start',
                  mb: 1
                }}
              >
                <Paper
                  elevation={1}
                  sx={{
                    p: 2,
                    maxWidth: '80%',
                    backgroundColor: msg.is_user ? 'primary.main' : 'secondary.main',
                    color: 'white'
                  }}
                >
                  <Typography>{msg.content}</Typography>
                </Paper>
                {msg.metadata?.warnings?.map((warning, i) => (
                  <Alert severity="warning" sx={{ mt: 1, width: '100%' }} key={i}>
                    {warning}
                  </Alert>
                ))}
              </ListItem>
            ))}
            <div ref={messagesEndRef} />
          </List>
        </Box>

        <form onSubmit={handleSendMessage}>
          <Box sx={{ 
            display: 'flex', 
            gap: 2,
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              '& fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.1)',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.2)',
              }
            }
          }}>
            <TextField
              fullWidth
              variant="outlined"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your message..."
              disabled={loading}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={loading || !message.trim()}
              sx={{ 
                minWidth: '100px',
                backgroundColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                }
              }}
            >
              {loading ? <CircularProgress size={24} /> : 'Send'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default ChatbotPage; 